import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { 
  CheckCircle, 
  TrendingUp, 
  Users, 
  Globe, 
  Shield, 
  Headphones,
  Rocket,
  Award,
  Zap
} from "lucide-react";

const WhyUs = () => {
  const features = [
    {
      icon: <TrendingUp className="w-6 h-6" />,
      title: "Affordable & Scalable Solutions",
      description: "Cost-effective plans that grow with your institution or business, from startup to enterprise level.",
      gradient: "from-blue-500 to-cyan-400",
      delay: 0.1
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: "Education & Startup Expertise",
      description: "Deep industry knowledge with 500+ successful projects across educational institutions and startups.",
      gradient: "from-purple-500 to-pink-400",
      delay: 0.2
    },
    {
      icon: <Zap className="w-6 h-6" />,
      title: "Modern Tech Stack",
      description: "Built with cutting-edge technologies including React, Node.js, AI integration, and cloud-native architecture.",
      gradient: "from-amber-500 to-orange-400",
      delay: 0.3
    },
    {
      icon: <Headphones className="w-6 h-6" />,
      title: "Long-Term Support",
      description: "99% client satisfaction with dedicated support, regular updates, and continuous improvement.",
      gradient: "from-emerald-500 to-teal-400",
      delay: 0.4
    },
    {
      icon: <Globe className="w-6 h-6" />,
      title: "Global Reach, Local Impact",
      description: "Serving 15+ countries with localized solutions tailored to regional educational needs.",
      gradient: "from-indigo-500 to-blue-400",
      delay: 0.5
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Enterprise-Grade Security",
      description: "Bank-level security protocols, GDPR compliance, and regular security audits for complete peace of mind.",
      gradient: "from-red-500 to-rose-400",
      delay: 0.6
    }
  ];

  const stats = [
    { value: "500+", label: "Projects Completed", icon: <Rocket className="w-5 h-5" /> },
    { value: "250+", label: "Happy Clients", icon: <Users className="w-5 h-5" /> },
    { value: "15+", label: "Countries Served", icon: <Globe className="w-5 h-5" /> },
    { value: "99%", label: "Client Satisfaction", icon: <Award className="w-5 h-5" /> }
  ];

  return (
    <>
      <Helmet>
        <title>Why Choose Us | Skyreti EduCore</title>
        <meta
          name="description"
          content="Discover why leading educational institutions and startups choose Skyreti EduCore for innovative digital solutions."
        />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
        {/* Hero Section */}
        <section className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-600/10 to-cyan-500/10" />
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="text-center"
            >
              <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
                Why Choose <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-cyan-500">Skyreti EduCore</span>
              </h1>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                We're not just another digital agency. We're your strategic partner in transforming education and business through innovative technology solutions.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-8 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
              {stats.map((stat, index) => (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: index * 0.1 }}
                  className="text-center p-4"
                >
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <div className="text-blue-600">{stat.icon}</div>
                    <div className="text-3xl font-bold text-gray-900">{stat.value}</div>
                  </div>
                  <div className="text-sm font-medium text-gray-600">{stat.label}</div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Features Grid */}
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Our Winning Differentiators
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Comprehensive solutions designed to elevate your digital presence and drive growth
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {features.map((feature, index) => (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: feature.delay }}
                  whileHover={{ y: -5, transition: { duration: 0.2 } }}
                  className="group relative bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100"
                >
                  <div className={`absolute top-0 left-0 w-full h-1 bg-gradient-to-r ${feature.gradient} rounded-t-2xl`} />
                  <div className={`inline-flex p-3 rounded-xl bg-gradient-to-br ${feature.gradient} bg-opacity-10 mb-4`}>
                    <div className={`text-transparent bg-clip-text bg-gradient-to-r ${feature.gradient}`}>
                      {feature.icon}
                    </div>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                  <div className="mt-6 pt-6 border-t border-gray-100 flex items-center justify-between">
                    <span className="text-sm font-medium text-blue-600">Learn more</span>
                    <CheckCircle className="w-5 h-5 text-green-500 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="bg-gradient-to-r from-blue-600 to-cyan-500 rounded-3xl p-8 md:p-12 text-center text-white"
            >
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Ready to Transform Your Digital Journey?
              </h2>
              <p className="text-lg mb-8 text-blue-50">
                Join 250+ satisfied clients who have elevated their digital presence with Skyreti EduCore.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="px-8 py-3 bg-white text-blue-600 font-semibold rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Get Started Today
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="px-8 py-3 bg-transparent border-2 border-white text-white font-semibold rounded-lg hover:bg-white/10 transition-colors"
                >
                  Explore Services
                </motion.button>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Trust Badges */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Trusted By Leading Institutions Worldwide
              </h3>
              <p className="text-gray-600">
                Universities, schools, and startups across 15+ countries rely on our expertise
              </p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-8">
              {["University Partners", "School Networks", "HR Tech", "EdTech Startups", "Content Platforms", "Government"].map((item) => (
                <div
                  key={item}
                  className="bg-white rounded-xl p-6 text-center shadow-sm hover:shadow-md transition-shadow"
                >
                  <div className="text-gray-900 font-semibold">{item}</div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default WhyUs;